import Header from '@/react-app/components/Header';
import Footer from '@/react-app/components/Footer';
import { FileText, Scale, AlertTriangle, Shield, Mail, Phone } from 'lucide-react';

export default function Terms() {
    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            <Header />

            {/* Hero Section */}
            <section className="pt-16 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="text-center mb-12">
                        <div className="bg-blue-100 dark:bg-blue-900 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                            <FileText className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                            Terms & Conditions
                        </h1>
                        <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                            Please read these terms carefully before using our financial services platform.
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                            Last updated: September 27, 2025
                        </p>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-lg max-w-none dark:prose-invert">

                        {/* Introduction */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Introduction</h2>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                These Terms and Conditions ("Terms") govern your use of the Trade Enrich platform and services ("Service") operated by Trade Enrich Inc. ("we," "our," or "us"). By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of these terms, you may not access the Service.
                            </p>
                        </div>

                        {/* Acceptance of Terms */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                                <Scale className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                                Acceptance of Terms
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                                By creating an account, accessing, or using our Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. You also represent that you are at least 18 years of age and have the legal capacity to enter into this agreement.
                            </p>
                        </div>

                        {/* Service Description */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Service Description</h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                                Trade Enrich provides a digital platform for savings and investment services, including:
                            </p>
                            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                <li>Savings packages with monthly returns</li>
                                <li>Account management and financial tracking</li>
                                <li>Referral bonus programs</li>
                                <li>Investment portfolio management</li>
                                <li>Financial education and resources</li>
                            </ul>
                        </div>

                        {/* Account Registration */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Account Registration and Security</h2>
                            <div className="space-y-4">
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Registration Requirements</h4>
                                    <p className="text-gray-600 dark:text-gray-300">To use our Service, you must provide accurate, complete, and current information during registration. You are responsible for maintaining the confidentiality of your account credentials.</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Know Your Customer (KYC)</h4>
                                    <p className="text-gray-600 dark:text-gray-300">We require identity verification to comply with financial regulations. You must provide valid identification documents and may be subject to additional verification procedures.</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Account Security</h4>
                                    <p className="text-gray-600 dark:text-gray-300">You are responsible for all activities that occur under your account. Notify us immediately of any unauthorized use or security breach.</p>
                                </div>
                            </div>
                        </div>

                        {/* Financial Services */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Financial Services and Investment Risks</h2>

                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-6 rounded-lg mb-6">
                                <div className="flex items-start">
                                    <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400 mr-3 mt-0.5" />
                                    <div>
                                        <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">Important Investment Notice</h4>
                                        <p className="text-amber-700 dark:text-amber-300 text-sm">
                                            All investments carry risk. Past performance does not guarantee future results. You should carefully consider your investment objectives and risk tolerance before participating in any savings or investment programs.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Savings Packages</h4>
                                    <p className="text-gray-600 dark:text-gray-300">Our savings packages offer projected returns based on current market conditions. Returns are not guaranteed and may vary based on market performance and economic factors.</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Deposits and Withdrawals</h4>
                                    <p className="text-gray-600 dark:text-gray-300">Deposits are subject to minimum amounts and verification requirements. Withdrawal requests may take 3-5 business days to process and may be subject to fees and restrictions.</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Fees and Charges</h4>
                                    <p className="text-gray-600 dark:text-gray-300">We may charge fees for certain services. All applicable fees will be clearly disclosed before you confirm any transaction.</p>
                                </div>
                            </div>
                        </div>

                        {/* Referral Program */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Referral Program</h2>
                            <div className="space-y-4">
                                <p className="text-gray-600 dark:text-gray-300">
                                    Our referral program allows you to earn bonuses for referring new users. Referral bonuses are calculated based on the deposits made by users you refer and are subject to the following conditions:
                                </p>
                                <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                    <li>Referred users must complete KYC verification</li>
                                    <li>Bonuses are paid only on verified deposits</li>
                                    <li>Referral fraud or abuse may result in account suspension</li>
                                    <li>We reserve the right to modify or terminate the referral program</li>
                                </ul>
                            </div>
                        </div>

                        {/* Prohibited Activities */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Prohibited Activities</h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                                You agree not to engage in any of the following prohibited activities:
                            </p>
                            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                <li>Providing false or misleading information</li>
                                <li>Money laundering or terrorist financing</li>
                                <li>Market manipulation or fraudulent trading</li>
                                <li>Unauthorized access to other users' accounts</li>
                                <li>Reverse engineering or attempting to extract source code</li>
                                <li>Violating applicable laws or regulations</li>
                                <li>Creating multiple accounts to abuse promotions</li>
                                <li>Using automated systems or bots</li>
                            </ul>
                        </div>

                        {/* Privacy and Data */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                                <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                                Privacy and Data Protection
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                Your privacy is important to us. Our collection, use, and disclosure of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using our Service, you consent to the collection and use of your information as outlined in our Privacy Policy.
                            </p>
                        </div>

                        {/* Limitation of Liability */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Limitation of Liability</h2>
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                                To the maximum extent permitted by law, Trade Enrich shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation:
                            </p>
                            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                <li>Loss of profits, data, or business opportunities</li>
                                <li>Service interruptions or delays</li>
                                <li>Third-party actions or market fluctuations</li>
                                <li>Unauthorized access to your account due to your negligence</li>
                            </ul>
                            <p className="text-gray-600 dark:text-gray-300 mt-4">
                                Our total liability shall not exceed the amount of fees you have paid to us in the twelve months preceding the claim.
                            </p>
                        </div>

                        {/* Indemnification */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Indemnification</h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                You agree to indemnify, defend, and hold harmless Trade Enrich and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.
                            </p>
                        </div>

                        {/* Governing Law */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Governing Law and Dispute Resolution</h2>
                            <div className="space-y-4">
                                <p className="text-gray-600 dark:text-gray-300">
                                    These Terms are governed by the laws of the State of New York, without regard to conflict of law principles.
                                </p>

                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Dispute Resolution</h4>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        Any disputes arising from these Terms or your use of the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except for claims that may be brought in small claims court.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Termination */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Termination</h2>
                            <div className="space-y-4">
                                <p className="text-gray-600 dark:text-gray-300">
                                    We may terminate or suspend your account immediately, without prior notice, for any reason, including breach of these Terms. Upon termination:
                                </p>
                                <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 ml-4">
                                    <li>Your right to use the Service will cease immediately</li>
                                    <li>We will process any pending withdrawals in accordance with our policies</li>
                                    <li>You remain liable for any outstanding obligations</li>
                                    <li>Provisions regarding liability, indemnification, and dispute resolution survive termination</li>
                                </ul>
                            </div>
                        </div>

                        {/* Changes to Terms */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Changes to Terms</h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                We reserve the right to modify these Terms at any time. We will notify you of any material changes by posting the updated Terms on our platform and updating the "Last updated" date. Your continued use of the Service after such changes constitutes acceptance of the new Terms.
                            </p>
                        </div>

                        {/* Contact Information */}
                        <div className="mb-12">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                                <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                                Contact Information
                            </h2>
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                                <p className="text-gray-600 dark:text-gray-300 mb-4">
                                    If you have any questions about these Terms and Conditions, please contact us:
                                </p>
                                <div className="space-y-2 text-gray-600 dark:text-gray-300">
                                    <div className="flex items-center">
                                        <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                                        <span>Email: support@tradeenrich.com</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                                        <span>Phone: +234 7063441554</span>
                                    </div>
                                    <div className="mt-4">
                                        <p className="font-medium">Mailing Address:</p>
                                        <p>Trade Enrich Privacy Team<br />
                                            Thomas and Paulina Ibe's New Compound by Nkwo Mbaise Market Umuodo Itu Autonomous Community Ezinihitte-Mbaise LGA, Imo State, Nigeria<br />
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
